/* eslint-disable no-console */
import * as THREE from 'three'
import { OrbitControls } from 'three/addons/controls/OrbitControls.js'
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js'
import { Capsule } from 'three/addons/math/Capsule.js'
import { Octree } from 'three/addons/math/Octree.js'
import metadata from './metadata'
import NavigationButtons from './navigation_buttons'
import { NexusObject } from './nexus_three'
import {
  isViewerFullscreenActive,
  requestViewerFullscreen,
  VIEWER_FULLSCREEN_CHANGE_EVENT,
} from './fullscreen'
import genTitleBox from './titleBox'
import genCreditsBox from './creditsBox'
import {
  setup as setupControls,
  simpleMaterialDisabled,
  textureDisabled,
  wireframeDisabled,
} from './viewer_controls'

// Shared variables
let camera, scene, renderer, container
const lights = [] // will contain directional/main configured lights
let ambientLight = null // single ambient fallback
const nxz = []
let threeDModels
let activeView = 0
const simpleMaterialName = 'default'
let currentMode = 'orbit' // Default
let flashlightLight = null
let torchLight = null
let candleLight = null
let cameraControls
let restoreFullscreenAfterPointerUnlock = false
let pendingFullscreenRestore = false
let fullscreenRestoreTimeoutIds = []

const nexusFilesRoot = `${process.env.API_BASE_URL}images/3d/db/`
const materials = {
  default: THREE.MeshStandardMaterial,
  phong: THREE.MeshPhongMaterial,
  depth: THREE.MeshDepthMaterial,
  normal: THREE.MeshNormalMaterial,
}
const wireframeMaterial = new THREE.PointsMaterial({
  size: 0.5,
  color: 0xffffff,
  transparent: false,
  opacity: 0.5,
})

// Free roam
const clock = new THREE.Clock()
const GRAVITY = 27
const STEPS_PER_FRAME = 3 // OPTIMIZED: Reduced from 5 for better performance
const worldOctree = new Octree()
const playerHeight = 1.5
const playerRadius = 0.35
const playerCollider = new Capsule(
  new THREE.Vector3(0, playerRadius, 0),
  new THREE.Vector3(0, playerHeight, 0),
  playerRadius
)
const playerVelocity = new THREE.Vector3()
const playerDirection = new THREE.Vector3()
export const keyStates = {}
let playerOnFloor = false

// Viewcube
let cubeScene, cubeCamera, cubeRenderer, cubeMesh, cubeCanvas
const cubeRaycaster = new THREE.Raycaster()
const cubeMouse = new THREE.Vector2()
let cubeSize = 120
let hoveredFaceIndex = -1
const cubeFaceDirections = {
  0: new THREE.Vector3(1, 0, 0), // Right
  1: new THREE.Vector3(-1, 0, 0), // Left
  2: new THREE.Vector3(0.0001, 1, 0), // Top
  3: new THREE.Vector3(0.0001, -1, 0), // Bottom
  4: new THREE.Vector3(0, 0, 1), // Front
  5: new THREE.Vector3(0, 0, -1), // Back
}

function createLabelTexture(label, renderer) {
  const dpr = window.devicePixelRatio || 1
  const size = 512
  const canvas = document.createElement('canvas')
  canvas.width = canvas.height = size * dpr
  const ctx = canvas.getContext('2d')

  ctx.scale(dpr, dpr)
  ctx.clearRect(0, 0, size, size)
  ctx.fillStyle = '#555'
  ctx.fillRect(0, 0, size, size)

  // Text styling
  ctx.fillStyle = '#fff'
  ctx.font = 'bold 96px Arial'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(label, size / 2, size / 2)

  const texture = new THREE.CanvasTexture(canvas)
  texture.minFilter = THREE.LinearFilter
  texture.magFilter = THREE.LinearFilter
  texture.anisotropy = renderer.capabilities.getMaxAnisotropy()

  return texture
}

function initViewCube() {
  if (cubeCanvas) return
  // Canvas & Renderer
  cubeSize = window.innerWidth < 768 ? 80 : 120
  cubeCanvas = document.createElement('canvas')
  cubeCanvas.width = cubeCanvas.height = cubeSize
  cubeCanvas = document.createElement('canvas')
  cubeCanvas.width = cubeCanvas.height = cubeSize
  cubeCanvas.style.position = 'absolute'
  cubeCanvas.style.top = '10px'
  cubeCanvas.style.right = '10px'
  cubeCanvas.style.zIndex = '999'
  cubeCanvas.style.pointerEvents = 'auto'
  container.appendChild(cubeCanvas)

  cubeRenderer = new THREE.WebGLRenderer({ canvas: cubeCanvas, antialias: true, alpha: true })
  cubeRenderer.setSize(cubeSize, cubeSize)

  // Scene and Camera
  cubeScene = new THREE.Scene()
  cubeCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 10)
  cubeCamera.position.set(2, 2, 2)
  cubeCamera.up.set(0, 1, 0)
  cubeCamera.lookAt(0, 0, 0)

  // Cube with face labels
  const cubeGeo = new THREE.BoxGeometry(1, 1, 1)

  // Create materials for each face
  const materials = [
    new THREE.MeshBasicMaterial({ color: 0xffffff, map: createLabelTexture('Right', renderer) }),
    new THREE.MeshBasicMaterial({ color: 0xffffff, map: createLabelTexture('Left', renderer) }),
    new THREE.MeshBasicMaterial({ color: 0xffffff, map: createLabelTexture('Top', renderer) }),
    new THREE.MeshBasicMaterial({ color: 0xffffff, map: createLabelTexture('Bottom', renderer) }),
    new THREE.MeshBasicMaterial({ color: 0xffffff, map: createLabelTexture('Front', renderer) }),
    new THREE.MeshBasicMaterial({ color: 0xffffff, map: createLabelTexture('Back', renderer) }),
  ]

  cubeMesh = new THREE.Mesh(new THREE.BoxGeometry(1, 1, 1), materials)
  cubeScene.add(cubeMesh)

  // Edges
  const edges = new THREE.EdgesGeometry(cubeGeo)
  const edgesMat = new THREE.LineBasicMaterial({ color: 0x000000 })
  const edgesMesh = new THREE.LineSegments(edges, edgesMat)
  cubeScene.add(edgesMesh)

  // Lights
  const ambient = new THREE.AmbientLight(0xffffff, 0.8)
  cubeScene.add(ambient)
  const dirLight = new THREE.DirectionalLight(0xffffff, 0.5)
  dirLight.position.set(3, 3, 3)
  cubeScene.add(dirLight)
  cubeCanvas.addEventListener('click', onCubeClick)
  cubeCanvas.addEventListener('mousemove', onCubeHover)
  cubeCanvas.addEventListener('mouseleave', onCubeMouseLeave)

  cubeCanvas.addEventListener('click', onCubeClick)
  cubeCanvas.addEventListener(
    'touchstart',
    e => {
      e.preventDefault()
      onCubeClick(e.touches[0])
    },
    { passive: false }
  )
}

function onCubeClick(event) {
  // Do not start a new animation if one is already running
  if (currentMode !== 'orbit' || (cameraControls && !cameraControls.enabled)) {
    return
  }

  const rect = cubeCanvas.getBoundingClientRect()
  cubeMouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1
  cubeMouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1

  cubeRaycaster.setFromCamera(cubeMouse, cubeCamera)
  const intersections = cubeRaycaster.intersectObject(cubeMesh)

  if (intersections.length > 0) {
    const faceIndex = Math.floor(intersections[0].faceIndex / 2)
    const targetDir = cubeFaceDirections[faceIndex].clone()
    animateCamera(targetDir)
  }
}

function onCubeHover(event) {
  const rect = cubeCanvas.getBoundingClientRect()
  cubeMouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1
  cubeMouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1
  cubeRaycaster.setFromCamera(cubeMouse, cubeCamera)
  const intersections = cubeRaycaster.intersectObject(cubeMesh)

  if (intersections.length > 0) {
    const newHoveredFaceIndex = Math.floor(intersections[0].faceIndex / 2)
    // If the hovered face has changed
    if (newHoveredFaceIndex !== hoveredFaceIndex) {
      // De-highlight the previously hovered face (if there was one)
      if (hoveredFaceIndex !== -1) {
        cubeMesh.material[hoveredFaceIndex].color.set(0xffffff)
      }
      // Highlight the new face
      hoveredFaceIndex = newHoveredFaceIndex
      cubeMesh.material[hoveredFaceIndex].color.set(0xadd8e6)
    }
  } else {
    onCubeMouseLeave()
  }
}

function onCubeMouseLeave() {
  if (hoveredFaceIndex !== -1) {
    cubeMesh.material[hoveredFaceIndex].color.set(0xffffff)
    hoveredFaceIndex = -1
  }
}

function animateCamera(targetDir, duration = 0.6) {
  const controls = cameraControls
  controls.enabled = false

  const target = new THREE.Vector3(0, 0, 0)

  // Convert current camera position into spherical
  const startSpherical = new THREE.Spherical().setFromVector3(camera.position.clone().sub(target))
  const radius = startSpherical.radius

  // Compute target spherical from the face direction
  const endPos = targetDir.clone().normalize().multiplyScalar(radius)
  const endSpherical = new THREE.Spherical().setFromVector3(endPos)

  // Avoid singularity at poles
  endSpherical.phi = THREE.MathUtils.clamp(endSpherical.phi, 0.0001, Math.PI - 0.0001)

  let t = 0
  function step() {
    t += 1 / (duration * 60)
    if (t > 1) t = 1

    const k = THREE.MathUtils.smootherstep(t, 0, 1)

    const cur = new THREE.Spherical()
    cur.radius = THREE.MathUtils.lerp(startSpherical.radius, endSpherical.radius, k)
    cur.theta = THREE.MathUtils.lerp(startSpherical.theta, endSpherical.theta, k)
    cur.phi = THREE.MathUtils.lerp(startSpherical.phi, endSpherical.phi, k)

    const newPos = new THREE.Vector3().setFromSpherical(cur).add(target)

    camera.position.copy(newPos)
    controls.target.copy(target)
    camera.lookAt(target)
    controls.update()

    if (t < 1) {
      requestAnimationFrame(step)
    } else {
      controls.enabled = true
    }
  }

  step()
}

function smoothCameraReset(controls, camera, duration = 0.6) {
  const startPos = camera.position.clone()
  const startTarget = controls.target.clone()

  const endPos = new THREE.Vector3(0, 0, 4)
  const endTarget = new THREE.Vector3(0, 0, 0)

  let t = 0
  const animateReset = () => {
    t += 1 / (60 * duration)
    const k = THREE.MathUtils.smoothstep(t, 0, 1)

    camera.position.lerpVectors(startPos, endPos, k)
    controls.target.lerpVectors(startTarget, endTarget, k)
    controls.update()

    if (t < 1) requestAnimationFrame(animateReset)
  }

  animateReset()
}

// PERFORMANCE: Reusable vectors to avoid garbage collection
const tempVec1 = new THREE.Vector3()
const tempVec2 = new THREE.Vector3()
const tempVec3 = new THREE.Vector3()
const candleRay = new THREE.Ray()
const candlePosition = new THREE.Vector3()

// PERFORMANCE: Mouse sensitivity tuning
const MOUSE_SENSITIVITY = 0.002
const CANDLE_FORWARD_OFFSET = 0.5
const CANDLE_WALL_CLEARANCE = 0.12
const CANDLE_MIN_DISTANCE = 0.05

function clearFullscreenRestoreTimers() {
  fullscreenRestoreTimeoutIds.forEach(timeoutId => {
    window.clearTimeout(timeoutId)
  })
  fullscreenRestoreTimeoutIds = []
}

function cancelPendingFullscreenRestore() {
  pendingFullscreenRestore = false
  clearFullscreenRestoreTimers()
}

function attemptFullscreenRestoreAfterPointerUnlock() {
  if (!pendingFullscreenRestore) return

  if (document.pointerLockElement === document.body) {
    cancelPendingFullscreenRestore()
    return
  }

  if (isViewerFullscreenActive()) {
    cancelPendingFullscreenRestore()
    return
  }

  requestViewerFullscreen({
    allowWindowFallback: false,
    allowCssFallback: true,
    suppressFailureLogs: true,
  })
}

function scheduleFullscreenRestoreAfterPointerUnlock() {
  pendingFullscreenRestore = true
  clearFullscreenRestoreTimers()

  ;[0, 120, 320].forEach(delay => {
    const timeoutId = window.setTimeout(() => {
      attemptFullscreenRestoreAfterPointerUnlock()
    }, delay)
    fullscreenRestoreTimeoutIds.push(timeoutId)
  })

  const finalizeTimeoutId = window.setTimeout(() => {
    cancelPendingFullscreenRestore()
  }, 700)
  fullscreenRestoreTimeoutIds.push(finalizeTimeoutId)
}

document.addEventListener('pointerlockchange', () => {
  const isPointerLocked = document.pointerLockElement === document.body

  if (isPointerLocked) {
    restoreFullscreenAfterPointerUnlock = isViewerFullscreenActive()
    cancelPendingFullscreenRestore()
    return
  }

  if (!restoreFullscreenAfterPointerUnlock) return
  restoreFullscreenAfterPointerUnlock = false

  if (isViewerFullscreenActive()) {
    cancelPendingFullscreenRestore()
    return
  }

  scheduleFullscreenRestoreAfterPointerUnlock()
})

window.addEventListener(VIEWER_FULLSCREEN_CHANGE_EVENT, () => {
  if (!pendingFullscreenRestore) return

  if (isViewerFullscreenActive()) {
    cancelPendingFullscreenRestore()
    return
  }

  scheduleFullscreenRestoreAfterPointerUnlock()
})

function isMoveForwardActive() {
  return Boolean(keyStates['KeyW'] || keyStates['ArrowUp'])
}

function isMoveBackwardActive() {
  return Boolean(keyStates['KeyS'] || keyStates['ArrowDown'])
}

function isMoveLeftActive() {
  return Boolean(keyStates['KeyA'] || keyStates['ArrowLeft'])
}

function isMoveRightActive() {
  return Boolean(keyStates['KeyD'] || keyStates['ArrowRight'])
}

function isViewerUiTarget(target) {
  if (!target?.closest) return false

  return Boolean(
    target.closest('#nexus-controls-container') ||
      target.closest('#joystick-zone') ||
      target.closest('#titleBoxWidget') ||
      target.closest('#creditsBoxWidget') ||
      target.closest('#freeroam-help-trigger') ||
      target.closest('#freeroam-help-overlay')
  )
}

// Helpers
function onInitialLoadComplete(nexusInstance) {
  if (currentMode === 'orbit') {
    const s = 1 / nexusInstance.geometry.boundingSphere.radius
    const target = new THREE.Vector3()
    const p = nexusInstance.geometry.boundingBox.getCenter(target).negate()
    nexusInstance.position.set(p.x * s, p.y * s, p.z * s)
    nexusInstance.scale.set(s, s, s)
    if (torchLight && torchLight.visible) {
      torchLight.position.copy(getModelGroundCenter()).add(new THREE.Vector3(0.7, 3, 0.7))
    }
    setTimeout(hideLoader, 2000)
  } else {
    setTimeout(hideLoader, 4000)
  }

  // Ensure material mode is applied to the newly loaded model
  applyMaterialModeToNexus(nexusInstance)
}

let loaderElement

function showLoader() {
  if (!loaderElement) {
    loaderElement = document.getElementById('loader-overlay')
  }
  if (renderer) renderer.domElement.style.opacity = '0'
  if (loaderElement) loaderElement.classList.remove('hidden')
}

function hideLoader() {
  if (!loaderElement) {
    loaderElement = document.getElementById('loader-overlay')
  }
  if (renderer) renderer.domElement.style.transition = 'opacity 0.5s'
  if (renderer) renderer.domElement.style.opacity = '1'
  if (loaderElement) loaderElement.classList.add('hidden')
}

function textureMaterial() {
  const texture = new THREE.DataTexture(new Uint8Array([1, 1, 1]), 1, 1, THREE.RGBAFormat)
  texture.needsUpdate = true
  return new THREE.MeshLambertMaterial({
    color: 0xffffff,
    map: texture,
  })
}

function resetPlayerPosition() {
  playerCollider.start.set(0, playerRadius, 0)
  playerCollider.end.set(0, playerHeight, 0)
  playerVelocity.set(0, 0, 0)
  camera.position.copy(playerCollider.end)
  camera.rotation.set(0, 0, 0)
}

function getModelGroundCenter() {
  if (!nxz[activeView]) return new THREE.Vector3(0, 20, 0)
  const nexusInstance = nxz[activeView]
  const box = nexusInstance.geometry?.boundingBox
  if (!box) {
    return nexusInstance.getWorldPosition(new THREE.Vector3())
  }

  // Center X/Z
  const centerLocal = box.getCenter(new THREE.Vector3())
  // Ground Y
  const groundY = box.min.y

  // Build position
  const groundPos = new THREE.Vector3(centerLocal.x, groundY, centerLocal.z)

  // Convert to world space
  nexusInstance.updateMatrixWorld(true)
  nexusInstance.localToWorld(groundPos)
  return groundPos
}

// Apply appropriate material to every mesh in the NexusObject
// Stores original material on mesh.userData._originalMaterial the first time
function applyMaterialModeToNexus(nexusInstance) {
  if (!nexusInstance) return
  nexusInstance.traverse(child => {
    if (child.isMesh) {
      // store original
      if (!child.userData._originalMaterial) {
        child.userData._originalMaterial = child.material
      }

      // choose material according to global flags
      if (wireframeDisabled === false) {
        try {
          child.material = wireframeMaterial
        } catch (e) {
          child.material = new THREE.MeshBasicMaterial({ wireframe: true })
        }
        child.material.needsUpdate = true
      } else if (textureDisabled === false) {
        if (child.userData._originalMaterial) {
          child.material = child.userData._originalMaterial
        } else {
          child.material = textureMaterial()
        }
        child.material.needsUpdate = true
      } else if (simpleMaterialDisabled === false) {
        const simpleMat = new THREE.MeshPhongMaterial({
          color: 0xffffff,
          specular: 0x222222,
          shininess: 30,
        })
        child.material = simpleMat
        child.material.needsUpdate = true
      } else {
        if (child.userData._originalMaterial) {
          child.material = child.userData._originalMaterial
          child.material.needsUpdate = true
        }
      }
    }
  })
}

function applyMaterialModeToAllNexus() {
  nxz.forEach(n => {
    if (n) applyMaterialModeToNexus(n)
  })
}

// Config stuff
function setConfiguration(config) {
  lights.forEach(l => {
    if (l.parent) l.parent.remove(l)
  })
  lights.length = 0
  if (ambientLight && ambientLight.parent) {
    ambientLight.parent.remove(ambientLight)
  }
  ambientLight = null

  if (config.lights) {
    config.lights.forEach(lightConfiguration => {
      const colorConfiguration = lightConfiguration.color
      const r = parseInt(colorConfiguration.r, 16) << 16
      const g = parseInt(colorConfiguration.g, 16) << 8
      const b = parseInt(colorConfiguration.b, 16)
      const color = r + g + b
      const light = new THREE.DirectionalLight(color, lightConfiguration.intensity)
      const p = lightConfiguration.position
      light.position.set(p.x, p.y, p.z)
      scene.add(light, light.target)
      lights.push(light)
    })
  }

  ambientLight = new THREE.AmbientLight(0x555555)
  applyMainLighting()
}

export function applyMainLighting() {
  lights.forEach(l => {
    if (l.parent) scene.remove(l, l.target)
  })
  if (ambientLight && ambientLight.parent) scene.remove(ambientLight)

  if (simpleMaterialDisabled === false) {
    lights.forEach(l => {
      scene.add(l, l.target)
      l.visible = true
    })
  } else {
    if (ambientLight) {
      scene.add(ambientLight)
      ambientLight.visible = true
    }
  }

  applyMaterialModeToAllNexus()
}

function updateMainLightsVisibility(visible) {
  if (simpleMaterialDisabled === false) {
    lights.forEach(l => {
      l.visible = visible
    })
  } else {
    if (ambientLight) ambientLight.visible = visible
  }
}

async function getConfigurationData() {
  lights.forEach(light => scene.remove(light, light.target))
  lights.length = 0
  if (ambientLight && ambientLight.parent) scene.remove(ambientLight)

  const base = process.env.API_BASE_URL || window.location.origin
  const baseUrl = base === '/' ? window.location.origin + '/' : base

  const modelPath = threeDModels[activeView].configurationUrl
  const finalModelConfigUrl = new URL(modelPath, baseUrl).href
  const finalDefaultConfigUrl = new URL(`${nexusFilesRoot}default.json`, baseUrl).href

  try {
    const response = await fetch(finalModelConfigUrl)
    if (!response.ok) throw new Error(`Failed to fetch model config: ${response.statusText}`)
    const config = await response.json()
    setConfiguration(config)
    return
  } catch (error) {
    console.warn(`Could not load model-specific config: ${error.message}. Trying default.json...`)
  }

  try {
    const response = await fetch(finalDefaultConfigUrl)
    if (!response.ok) throw new Error(`Failed to fetch default config: ${response.statusText}`)
    const config = await response.json()
    setConfiguration(config)
    return
  } catch (error) {
    console.warn(`Could not load default.json: ${error.message}. Setting hardcoded light.`)
  }

  ambientLight = new THREE.AmbientLight(0x555555)
  scene.add(ambientLight)
}

function updateButtonsState(currentView, numberOfViews) {
  const prevPageButton = document.getElementById('prevPage')
  const nextPageButton = document.getElementById('nextPage')
  const navigationButtonsContainer = document.getElementById('navigationButtons')
  if (numberOfViews <= 1) {
    navigationButtonsContainer.style.display = 'none'
    return
  }
  navigationButtonsContainer.style.display = 'flex'
  prevPageButton.classList.toggle('disabled', currentView === 0)
  nextPageButton.classList.toggle('disabled', currentView === numberOfViews - 1)
}

function resetSceneForNewModel() {
  if (nxz[activeView]) {
    scene.remove(nxz[activeView])
  }
  getConfigurationData()
}

function resetCameraPosition() {
  if (currentMode === 'orbit' && cameraControls) {
    cameraControls.reset()
    camera.position.set(0, 0, 4)
    camera.updateProjectionMatrix()
  }
}

function nextFunction() {
  if (activeView >= threeDModels.length - 1) return
  showLoader()
  resetSceneForNewModel()
  activeView++
  if (currentMode === 'orbit') resetCameraPosition()
  if (!nxz[activeView]) {
    nxz[activeView] = new NexusObject(
      threeDModels[activeView].modelUrl,
      onInitialLoadComplete,
      () => {},
      renderer,
      textureMaterial()
    )
  }
  scene.add(nxz[activeView])
  if (torchLight && torchLight.visible) {
    torchLight.position.copy(getModelGroundCenter()).add(new THREE.Vector3(0.7, 3, 0.7))
  }
  updateButtonsState(activeView, threeDModels.length)
  genTitleBox.genTitleBox(threeDModels[activeView].sgtt)
}

function prevFunction() {
  if (activeView <= 0) return
  showLoader()
  resetSceneForNewModel()
  activeView--
  if (currentMode === 'orbit') resetCameraPosition()
  scene.add(nxz[activeView])
  if (torchLight && torchLight.visible) {
    torchLight.position.copy(getModelGroundCenter()).add(new THREE.Vector3(0.7, 3, 0.7))
  }
  if (currentMode === 'orbit') {
    const nexusInstance = nxz[activeView]
    const s = 1 / nexusInstance.geometry.boundingSphere.radius
    const target = new THREE.Vector3()
    const p = nexusInstance.geometry.boundingBox.getCenter(target).negate()
    nexusInstance.position.set(p.x * s, p.y * s, p.z * s)
    nexusInstance.scale.set(s, s, s)
  }
  setTimeout(hideLoader, 100)
  updateButtonsState(activeView, threeDModels.length)
  genTitleBox.genTitleBox(threeDModels[activeView].sgtt)
}

function initOrbitMode() {
  camera = new THREE.PerspectiveCamera(30, window.innerWidth / window.innerHeight, 0.1, 100)
  camera.position.set(0, 0, 4)
  camera.updateProjectionMatrix()
  cameraControls = new OrbitControls(camera, renderer.domElement)

  const hemiLight = new THREE.HemisphereLight(0xffffff, 0x444444, 1.3)
  hemiLight.name = 'orbit_hemi_light'
  scene.add(hemiLight)

  // Damping makes it smooth
  cameraControls.autoRotate = false
  cameraControls.enableDamping = true
  cameraControls.dampingFactor = 0.2

  // Speeds and limits
  cameraControls.rotateSpeed = 0.4
  cameraControls.zoomSpeed = 1.2
  cameraControls.panSpeed = 0.8

  // Allow zooming and panning
  cameraControls.enableZoom = true
  cameraControls.enablePan = true

  // Limit vertical rotation — prevents going under model
  const degToRad = degrees => (degrees * Math.PI) / 180
  cameraControls.minPolarAngle = degToRad(1)
  cameraControls.maxPolarAngle = degToRad(179)

  // Keep camera "upright" — prevents Z-axis roll
  cameraControls.screenSpacePanning = false
  cameraControls.target.set(0, 0, 0)
  cameraControls.update()
}

// Snapshot function
export function takeSnapshot(width = 3840, height = 2160) {
  if (!renderer || !scene || !camera) return
  const originalSize = renderer.getSize(new THREE.Vector2())
  const originalPixelRatio = renderer.getPixelRatio()

  renderer.setSize(width, height, false)
  renderer.setPixelRatio(1)
  renderer.render(scene, camera)

  const dataURL = renderer.domElement.toDataURL('image/png')

  renderer.setSize(originalSize.x, originalSize.y, false)
  renderer.setPixelRatio(originalPixelRatio)

  const link = document.createElement('a')
  link.href = dataURL
  link.download = `snapshot_${new Date().toISOString().replace(/[:.]/g, '-')}.png`
  link.click()
}

function initFreeRoamMode() {
  camera = new THREE.PerspectiveCamera(74, window.innerWidth / window.innerHeight, 0.1, 8000)
  camera.rotation.order = 'YXZ'

  const loader = new GLTFLoader()
  loader.load('static/tivoli/collision-map.glb', gltf => {
    gltf.scene.traverse(child => {
      if (child.isMesh) {
        child.castShadow = false
        child.receiveShadow = false
      }
    })
    gltf.scene.visible = false
    worldOctree.fromGraphNode(gltf.scene)
  })

  const handleKeyboardState = (event, isPressed) => {
    keyStates[event.code] = isPressed

    if (
      ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Space'].includes(event.code) &&
      event.cancelable
    ) {
      event.preventDefault()
    }
  }

  document.addEventListener('keydown', event => handleKeyboardState(event, true))
  document.addEventListener('keyup', event => handleKeyboardState(event, false))

  container.addEventListener('mousedown', event => {
    if (!isViewerUiTarget(event.target)) {
      document.body.requestPointerLock()
    }
  })

  document.body.addEventListener('mousemove', event => {
    if (document.pointerLockElement === document.body) {
      camera.rotation.y -= event.movementX * MOUSE_SENSITIVITY
      camera.rotation.x -= event.movementY * MOUSE_SENSITIVITY
      camera.rotation.x = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, camera.rotation.x))
    }
  })

  scene.children = scene.children.filter(obj => obj.name !== 'orbit_hemi_light')

  let lastTouchX = 0
  let lastTouchY = 0

  container.addEventListener(
    'touchstart',
    e => {
      if (isViewerUiTarget(e.target)) {
        return
      }
      lastTouchX = e.touches[0].pageX
      lastTouchY = e.touches[0].pageY
    },
    { passive: true }
  )

  container.addEventListener(
    'touchmove',
    e => {
      if (isViewerUiTarget(e.target)) {
        return
      }

      const touch = e.touches[0]
      const deltaX = touch.pageX - lastTouchX
      const deltaY = touch.pageY - lastTouchY

      lastTouchX = touch.pageX
      lastTouchY = touch.pageY

      camera.rotation.y += deltaX * MOUSE_SENSITIVITY * 1.5
      camera.rotation.x += deltaY * MOUSE_SENSITIVITY * 1.5
      camera.rotation.x = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, camera.rotation.x))
    },
    { passive: true }
  )
}

// Free roam logic
function playerCollisions() {
  const result = worldOctree.capsuleIntersect(playerCollider)
  playerOnFloor = false
  if (result) {
    playerOnFloor = result.normal.y > 0
    if (!playerOnFloor) {
      playerVelocity.addScaledVector(result.normal, -result.normal.dot(playerVelocity))
    }
    if (result.depth >= 1e-10) {
      playerCollider.translate(result.normal.multiplyScalar(result.depth))
    }
  }
}

function updatePlayer(deltaTime) {
  // Stronger damping for quicker stops and better control
  let damping = Math.exp(-10 * deltaTime) - 1

  if (!playerOnFloor) {
    playerVelocity.y -= GRAVITY * deltaTime
    // Less air resistance for better feel
    damping *= 0.7
  }

  playerVelocity.addScaledVector(playerVelocity, damping)
  const deltaPosition = playerVelocity.clone().multiplyScalar(deltaTime)
  playerCollider.translate(deltaPosition)
  playerCollisions()
  camera.position.copy(playerCollider.end)
}

// Reuse temp vectors to avoid garbage collection
function getForwardVector() {
  camera.getWorldDirection(tempVec1)
  tempVec1.y = 0
  tempVec1.normalize()
  return tempVec1
}

function getSideVector() {
  camera.getWorldDirection(tempVec2)
  tempVec2.y = 0
  tempVec2.normalize()
  tempVec2.cross(camera.up)
  return tempVec2
}

function updateCandleLightPosition() {
  if (!camera || !candleLight) return

  const forwardVector = getForwardVector()
  const desiredDistance = CANDLE_FORWARD_OFFSET

  candleRay.origin.copy(camera.position)
  candleRay.direction.copy(forwardVector)

  let clampedDistance = desiredDistance
  const wallHit = worldOctree.rayIntersect(candleRay)

  if (wallHit && wallHit.distance <= desiredDistance + CANDLE_WALL_CLEARANCE) {
    clampedDistance = Math.max(CANDLE_MIN_DISTANCE, wallHit.distance - CANDLE_WALL_CLEARANCE)
  }

  candlePosition.copy(camera.position).add(forwardVector.multiplyScalar(clampedDistance))
  candleLight.position.copy(candlePosition)
}

function controls(deltaTime) {
  const moveForwardActive = isMoveForwardActive()
  const sprintActive = keyStates['ShiftLeft'] || keyStates['ShiftRight']
  const isRunning = sprintActive && moveForwardActive
  const targetSpeed = isRunning ? 8 : playerOnFloor ? 4.5 : 2 // Reduced to reasonable speeds
  const acceleration = playerOnFloor ? 30 : 15 // Moderate acceleration
  const speedDelta = deltaTime * acceleration

  if (moveForwardActive) {
    tempVec3.copy(getForwardVector()).multiplyScalar(speedDelta)
    playerVelocity.add(tempVec3)
  }
  if (isMoveBackwardActive()) {
    tempVec3.copy(getForwardVector()).multiplyScalar(-speedDelta)
    playerVelocity.add(tempVec3)
  }
  if (isMoveLeftActive()) {
    tempVec3.copy(getSideVector()).multiplyScalar(-speedDelta)
    playerVelocity.add(tempVec3)
  }
  if (isMoveRightActive()) {
    tempVec3.copy(getSideVector()).multiplyScalar(speedDelta)
    playerVelocity.add(tempVec3)
  }

  // Cap horizontal velocity for better control
  const horizontalSpeed = Math.sqrt(playerVelocity.x ** 2 + playerVelocity.z ** 2)
  if (horizontalSpeed > targetSpeed) {
    const ratio = targetSpeed / horizontalSpeed
    playerVelocity.x *= ratio
    playerVelocity.z *= ratio
  }

  if (playerOnFloor && keyStates['Space']) playerVelocity.y = 7
}

function teleportPlayerIfOob() {
  if (camera.position.y <= -25) {
    playerCollider.start.set(0, 0.35, 0)
    playerCollider.end.set(0, 1, 0)
    playerVelocity.set(0, 0, 0) // Reset velocity to prevent momentum
    camera.position.copy(playerCollider.end)
    camera.rotation.set(0, 0, 0)
  }
}

// Better delta time handling for consistent framerate
function freeRoamUpdate() {
  const deltaTime = Math.min(0.05, clock.getDelta()) / STEPS_PER_FRAME
  for (let i = 0; i < STEPS_PER_FRAME; i++) {
    controls(deltaTime)
    updatePlayer(deltaTime)
    teleportPlayerIfOob()
  }
}

function render() {
  Nexus.beginFrame(renderer.getContext())
  renderer.render(scene, camera)
  Nexus.endFrame(renderer.getContext())

  if (currentMode === 'orbit') {
    cubeCamera.position.copy(camera.position).normalize().multiplyScalar(2)
    cubeCamera.lookAt(0, 0, 0)
    cubeRenderer.render(cubeScene, cubeCamera)
  }
}

export function toggleFlashlight() {
  const nextLight = flashlightLight?.visible ? null : 'flashlight'
  setFreeroamLight(nextLight)
  return Boolean(flashlightLight?.visible)
}

export function toggleTorch() {
  const nextLight = torchLight?.visible ? null : 'torch'
  setFreeroamLight(nextLight)
  return Boolean(torchLight?.visible)
}

export function toggleCandle() {
  const nextLight = candleLight?.visible ? null : 'candle'
  setFreeroamLight(nextLight)
  return Boolean(candleLight?.visible)
}

function ensureFlashlightLight() {
  if (!flashlightLight) {
    flashlightLight = new THREE.SpotLight(0xffd8a1, 1, 40, Math.PI / 10, 1, 2)
    flashlightLight.castShadow = false
    flashlightLight.visible = false
    scene.add(flashlightLight)
    scene.add(flashlightLight.target)
  }

  return flashlightLight
}

function ensureTorchLight() {
  if (!torchLight) {
    torchLight = new THREE.PointLight(0xffd8a1, 1, 15, 2)
    torchLight.castShadow = false
    torchLight.visible = false
    scene.add(torchLight)
  }

  return torchLight
}

function ensureCandleLight() {
  if (!candleLight) {
    candleLight = new THREE.PointLight(0xffcfa3, 1.2, 6, 2)
    candleLight.castShadow = false
    candleLight.visible = false
    scene.add(candleLight)
  }

  return candleLight
}

export function setFreeroamLight(lightType = null) {
  if (lightType === 'flashlight') {
    ensureFlashlightLight()
  }
  if (lightType === 'torch') {
    ensureTorchLight()
  }
  if (lightType === 'candle') {
    ensureCandleLight()
  }

  if (flashlightLight) {
    flashlightLight.visible = lightType === 'flashlight'
  }
  if (torchLight) {
    torchLight.visible = lightType === 'torch'
  }
  if (candleLight) {
    candleLight.visible = lightType === 'candle'
  }

  updateMainLightsVisibility(lightType === null)

  if (ambientLight && ambientLight.parent) {
    ambientLight.visible = lightType === 'flashlight' || lightType === null
  }

  if (lightType === 'torch' && torchLight) {
    torchLight.position.copy(getModelGroundCenter()).add(new THREE.Vector3(0.7, 3, 0.7))
  }

  if (lightType === 'candle' && candleLight) {
    updateCandleLightPosition()
  }

  return lightType
}

export function hasAnyFreeroamLightActive() {
  return Boolean(
    (flashlightLight && flashlightLight.visible) ||
      (torchLight && torchLight.visible) ||
      (candleLight && candleLight.visible)
  )
}

export function disableAllFreeroamLights() {
  const hadActiveLight = hasAnyFreeroamLightActive()
  if (!hadActiveLight) return false

  setFreeroamLight(null)
  return true
}

function animate() {
  requestAnimationFrame(animate)
  if (currentMode === 'freeroam') {
    freeRoamUpdate()

    // FLASHLIGHT: SpotLight follows camera & direction
    if (flashlightLight && flashlightLight.visible) {
      flashlightLight.position.copy(camera.position)
      flashlightLight.target.position
        .copy(camera.position)
        .add(camera.getWorldDirection(new THREE.Vector3()).multiplyScalar(10))
    }

    // TORCH: static at model center, flicker in intensity
    if (torchLight && torchLight.visible) {
      torchLight.position.copy(getModelGroundCenter()).add(new THREE.Vector3(0.7, 3, 0.7))

      const baseIntensity = 1.2
      const flicker = Math.sin(Date.now() * 0.005) * 0.1 + (Math.random() - 0.5) * 0.1
      torchLight.intensity = Math.max(0.1, baseIntensity + flicker)
    }

    // CANDLE: small POV-following flicker
    if (candleLight && candleLight.visible) {
      updateCandleLightPosition()

      const baseIntensity = 1
      const flicker = Math.sin(Date.now() * 0.02) * 0.05 + (Math.random() - 0.5) * 0.06
      candleLight.intensity = baseIntensity + flicker * 0.5
      candleLight.distance = 10
      candleLight.decay = 2
    }
  } else {
    cameraControls.update()
  }

  render()
}

function onWindowResize() {
  camera.aspect = window.innerWidth / window.innerHeight
  camera.updateProjectionMatrix()
  renderer.setSize(window.innerWidth, window.innerHeight)
  if (currentMode === 'orbit') {
    cameraControls.handleResize()
  }
}

function updateAmbientLight() {
  const anyHandLightOn =
    (flashlightLight && flashlightLight.visible) || (torchLight && torchLight.visible)
  if (anyHandLightOn) {
    if (!ambientLight) {
      ambientLight = new THREE.AmbientLight(0x555555)
      scene.add(ambientLight)
    } else if (!ambientLight.parent) {
      scene.add(ambientLight)
    }
    ambientLight.visible = true
  } else {
    if (ambientLight && ambientLight.parent && simpleMaterialDisabled === false) {
      scene.remove(ambientLight)
    }
  }
}
export function getModelRadius() {
  const n = nxz[activeView]
  if (!n || !n.geometry || !n.geometry.boundingSphere) return 1
  return n.geometry.boundingSphere.radius
}

async function loadMultipleModels(data) {
  showLoader()
  const urlParams = new URLSearchParams(window.location.search)
  currentMode = urlParams.get('mode') === 'freeroam' ? 'freeroam' : 'orbit'
  threeDModels = data.modelsLinks
  container = document.getElementById('widgetContainer')
  scene = new THREE.Scene()
  scene.fog = new THREE.Fog(0x050505, 2000, 3500)

  renderer = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true })
  renderer.setPixelRatio(window.devicePixelRatio)
  renderer.setSize(window.innerWidth, window.innerHeight)
  renderer.setClearColor(scene.fog.color)
  renderer.shadowMap.enabled = true
  container.appendChild(renderer.domElement)

  // Prevent mobile scroll/pan hijacking touch on the viewer canvas
  renderer.domElement.style.touchAction = 'none'
  renderer.domElement.addEventListener('touchstart', e => e.preventDefault(), { passive: false })
  renderer.domElement.addEventListener('touchmove',  e => e.preventDefault(), { passive: false })

  if (currentMode === 'freeroam') {
    initFreeRoamMode()
  } else {
    initOrbitMode()
    initViewCube()
  }

  await getConfigurationData()
  nxz[0] = new NexusObject(
    threeDModels[0].modelUrl,
    onInitialLoadComplete,
    () => {},
    renderer,
    textureMaterial()
  )
  scene.add(nxz[0])
  let resetFunction

  if (currentMode === 'freeroam') {
    resetFunction = resetPlayerPosition
  } else {
    resetFunction = () => {
      if (cameraControls) {
        smoothCameraReset(cameraControls, camera)
      }
    }
  }

  applyMainLighting()
  updateAmbientLight()

  await setupControls({
    lights: lights,
    resetFunction: resetFunction,
    currentMode: currentMode,
    toggleFlashlight: toggleFlashlight,
    toggleTorch: toggleTorch,
    toggleCandle: toggleCandle,
    camera,
    scene,
    canSnapshot: await metadata.getSnapshotPermission(),
  })

  NavigationButtons.navigationButtons(nextFunction, prevFunction)
  updateButtonsState(activeView, threeDModels.length)
  genTitleBox.genTitleBox(threeDModels[activeView].sgtt)
  await genCreditsBox.renderCreditsBox()
  window.addEventListener('resize', onWindowResize, false)
  animate()
}

export default { loadMultipleModels, toggleFlashlight, toggleTorch, applyMainLighting }
